import { Alert, Button, Drawer, Flex, Form, Input, Switch } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { OutboundWebhookRead } from 'src/api/main'

import { EventSelector } from './EventSelector'
import type { EventGroup } from './hooks'
import { isSquadManaged } from './utils'

const MIN_SECRET_LENGTH = 32

/** Nothing in this form is a credential of the person filling it in, so no manager should fill it. */
const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': true
} as const

export type WebhookFormValues = {
  name?: string
  url: string
  events: string[]
  is_active: boolean
  custom_secret?: string
}

type Props = {
  open: boolean
  /** null = create */
  webhook: OutboundWebhookRead | null
  catalogue: {
    groups: EventGroup[] | undefined
    isLoading: boolean
    isError: boolean
  }
  submitting: boolean
  onSubmit: (values: WebhookFormValues) => void
  onClose: () => void
}

export const WebhookFormDrawer = ({
  open,
  webhook,
  catalogue,
  submitting,
  onSubmit,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<WebhookFormValues>()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      name: webhook?.name ?? '',
      url: webhook?.url ?? '',
      events: webhook ? [...webhook.events] : [],
      is_active: webhook?.is_active ?? true,
      custom_secret: ''
    })
  }, [open, webhook, form])

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      destroyOnHidden
      title={
        webhook
          ? t('workspace.apps.outboundWebhooks.form.editTitle', 'Edit webhook')
          : t('workspace.apps.outboundWebhooks.form.createTitle', 'New webhook')
      }
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button size="small" onClick={onClose}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            size="small"
            type="primary"
            loading={submitting}
            onClick={() => form.submit()}
          >
            {t('button.save', 'Save')}
          </Button>
        </Flex>
      }
    >
      {webhook && isSquadManaged(webhook) && (
        // Editable on purpose (Core is the source of truth): the warning is the only guard.
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t(
            'workspace.apps.outboundWebhooks.managed.editWarning',
            'Squad manages this webhook — changing the address or secret disconnects the agent.'
          )}
        />
      )}
      <Form form={form} layout="vertical" requiredMark onFinish={onSubmit}>
        <Form.Item
          label={t('workspace.apps.outboundWebhooks.form.name', 'Name')}
          name="name"
          rules={[{ max: 200 }]}
        >
          <Input
            placeholder={t(
              'workspace.apps.outboundWebhooks.form.namePlaceholder',
              'For example, Lead routing'
            )}
            allowClear
            {...NO_AUTOFILL}
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.outboundWebhooks.form.url', 'Endpoint URL')}
          name="url"
          rules={[
            { required: true },
            {
              // Format only. The address check that matters runs in CRM immediately before each
              // delivery, because DNS answers change between saving a URL and using it.
              pattern: /^https:\/\/.+/,
              message: t(
                'workspace.apps.outboundWebhooks.form.urlHttps',
                'Only https URLs are allowed.'
              )
            }
          ]}
        >
          <Input
            type="url"
            placeholder="https://example.com/hooks/crm"
            allowClear
            {...NO_AUTOFILL}
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.outboundWebhooks.form.events', 'Events')}
          name="events"
          rules={[
            {
              required: true,
              type: 'array',
              min: 1,
              message: t(
                'workspace.apps.outboundWebhooks.form.eventsRequired',
                'Select at least one event.'
              )
            }
          ]}
        >
          <EventSelector
            groups={catalogue.groups}
            isLoading={catalogue.isLoading}
            isError={catalogue.isError}
          />
        </Form.Item>

        <Form.Item
          label={t(
            'workspace.apps.outboundWebhooks.form.secret',
            'Signing secret'
          )}
          name="custom_secret"
          // The placeholder says what happens if the field is left alone, so the hint below only
          // has to say why anyone would fill it in. On edit that is a warning, not an invitation.
          extra={
            webhook
              ? t(
                  'workspace.apps.outboundWebhooks.form.secretEditHint',
                  'Replacing it breaks verification for whoever receives these events until they update it too.'
                )
              : t(
                  'workspace.apps.outboundWebhooks.form.secretCreateHint',
                  'Fill this in only if the receiving system requires its own value.'
                )
          }
          rules={[
            {
              min: MIN_SECRET_LENGTH,
              // `min`, not `count`: `count` is i18next's pluralization trigger, and this key has
              // no plural forms -- it only resolved by falling back to the unsuffixed key.
              message: t(
                'workspace.apps.outboundWebhooks.form.secretTooShort',
                'The secret must be at least {{min}} characters.',
                { min: MIN_SECRET_LENGTH }
              )
            }
          ]}
        >
          {/*
            Deliberately not Input.Password. A masked field next to a text field is what Chrome
            treats as a login form, and it autofilled the user's saved email into the URL and
            their password here -- autoComplete="off" does not stop that on a password input.
            Masking buys nothing anyway: this value is pasted in by its owner, and reading a
            stored secret goes through the audited reveal action, not this form.
          */}
          <Input
            placeholder={
              webhook
                ? t(
                    'workspace.apps.outboundWebhooks.form.secretPlaceholderEdit',
                    'Leave empty to keep the current secret'
                  )
                : t(
                    'workspace.apps.outboundWebhooks.form.secretPlaceholderCreate',
                    'Leave empty — we will generate one'
                  )
            }
            {...NO_AUTOFILL}
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.outboundWebhooks.form.active', 'Active')}
          name="is_active"
          valuePropName="checked"
          extra={t(
            'workspace.apps.outboundWebhooks.form.activeHint',
            'Turning this off stops deliveries immediately, including attempts already queued for retry.'
          )}
        >
          <Switch size="small" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
